declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
    };
  }
}

export type BookingTrackingPayload = {
  bookingId: number;
  value: number;
  currency: string;
  paymentPath: 'stripe' | 'pay_later';
  tripId?: number;
  boatId?: number;
};

const trackedBookingKey = (bookingId: number) =>
  `santtai_tracked_booking_${bookingId}`;

function hasTrackedBooking(bookingId: number): boolean {
  try {
    return sessionStorage.getItem(trackedBookingKey(bookingId)) === '1';
  } catch {
    return false;
  }
}

function markBookingTracked(bookingId: number): void {
  try {
    sessionStorage.setItem(trackedBookingKey(bookingId), '1');
  } catch {
    // Ignore storage failures in private browsing.
  }
}

export function trackBookingCompleted(
  payload: BookingTrackingPayload,
): boolean {
  if (hasTrackedBooking(payload.bookingId)) {
    return false;
  }

  window.posthog?.capture('booking_completed', {
    booking_id: payload.bookingId,
    value: payload.value,
    currency: payload.currency,
    payment_path: payload.paymentPath,
    trip_id: payload.tripId,
    boat_id: payload.boatId,
  });

  markBookingTracked(payload.bookingId);
  return true;
}

export function trackPurchase(
  payload: BookingTrackingPayload,
  onComplete?: () => void,
): void {
  trackBookingCompleted(payload);

  const gtagFn = window.gtag;
  if (!gtagFn) {
    onComplete?.();
    return;
  }

  gtagFn('event', 'purchase', {
    transaction_id: String(payload.bookingId),
    value: payload.value,
    currency: payload.currency,
    event_callback: onComplete,
  });
}

export function trackBookingStart(properties?: Record<string, unknown>): void {
  window.posthog?.capture('booking_start', properties);
}

export function trackEnquiryStart(properties?: Record<string, unknown>): void {
  window.posthog?.capture('enquiry_start', properties);
}
